import React, { useEffect, useState } from 'react';
import { Avatar, Backdrop, Box, Divider, Rating, TextField, Typography } from "@mui/material";
import CheckoutHeader from '../components/CheckoutHeader';
import { useAppDispatch, useAppSelector } from '../hooks';
import StyledPaper from '../components/StyledPaper';
import { t } from '@lingui/macro';
import dayjs from 'dayjs';
import { PaymentMethod, OrderStatus, ShippingMethod, TMenuItem } from '../types';
import api, { getFixedImageUrl } from '../api';
import PriceRender from '../components/PriceRender';
import parsePrice from '../utils/parsePrice';
import { ReactComponent as CheckoutFigures } from '../assets/statusFigures.svg'
import Loader from '../components/Loader';
import StyledButton from '../components/StyledButton';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import appRoutes from '../utils/routes';
import orderSlice from '../store/orderSlice';
import currentOrderSlice from '../store/orderSlice';
import currentUserSlice from '../store/userSlice';
import { ComponentType, TComponentCategory, TComponentItem } from '../types';

const OrderItemSummary: React.FC<{ item: TMenuItem }> = (props) => {
    const { item, item: { imagePath, name, orderCount, components } } = props;

    const cartDescription = (item: TMenuItem): string[] => {

        var itemDescription: string[] = [];

        for (let i = 0; i < item.components.length; i++) {
            const component = item.components[i];

            if (component.isBase) { continue }

            itemDescription.push(descriptionForComponent(component));
        }

        return itemDescription
    }

    const descriptionForComponent = (component: TComponentCategory): string => {

        const totalSelected = component.options.reduce((acc, value) => acc + value.value, 0)

        const optionsDescription: string[] = []
        const supportsAggregation = component.maxSelected == component.options.length

        if (component.options.length == 1) {
            const option = component.options[0]
            const description = descriptionForOption(option);
            if (description != null) {
                optionsDescription.push(description)
            }
        } else if (component.options.length == totalSelected && supportsAggregation) {
            optionsDescription.push(t`all`)
        } else {
            for (let i = 0; i < component.options.length; i++) {
                const option = component.options[i]
                const description = descriptionForOption(option);
                if (description != null) {
                    optionsDescription.push(description)
                }
            }
        }

        if (optionsDescription.length == 0) {
            return component.name.toLocaleLowerCase() + ": " + t`without`
        }

        return component.name + ": " + optionsDescription.join(", ")
    }

    const descriptionForOption = (option: TComponentItem): string | null => {

        if (option.value == 1) {
            return option.name.toLowerCase()
        } else if (option.value > 1) {
            return option.name.toLowerCase() + " x" + option.value
        }
        return null
    }

    var itemTitle = name;
    const baseComponent = components.filter((value) => value.isBase)[0];
    const componentSelectedOption = baseComponent?.options?.filter((value) => value.value > 0)[0];
    if (componentSelectedOption?.name) {
        itemTitle = name + " " + componentSelectedOption.name;
    }

    var itemDescription = cartDescription(item);


    return <Box display={'flex'}>
        <Box pr='12px'>
            <Avatar sx={{ width: '40px', height: '40px' }} src={getFixedImageUrl(imagePath)} alt={`${name} image`} />
        </Box>
        <Box flexGrow={1}>
            <Typography>{itemTitle}</Typography>
            {itemDescription.map(value => <Typography color={'grey'}>{value}</Typography>)}
        </Box>
        <Box>
            <Typography>{`${orderCount} x `}</Typography>
            <PriceRender price={parsePrice(item)} />
        </Box>
    </Box>
}

const StatusIndicator: React.FC<{ status: OrderStatus }> = (props) => {
    const statusMessages = {
        [OrderStatus.pending]: t`Your order is being processed`,
        [OrderStatus.inProgress]: t`Your order is being prepared`,
        [OrderStatus.completed]: t`Your order is ready`,
        [OrderStatus.failed]: t`Something went wrong`,
        [OrderStatus.canceled]: t`You cancelled this order`,
        [OrderStatus.declined]: t`Restaurant can’t prepare your order at the moment.`,
    }

    return <>
        <Typography color={'#ffffff'}>{statusMessages[props.status]}</Typography>
        <Box mb={'16px'} />
    </>
}

const OrderStatusScreen: React.FC = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const currentOrder = useAppSelector(state => state.order)
    const currentRestaurant = useAppSelector(state => state.restaurant.info)
    const currentUser = useAppSelector(state => state.user)
    const [rating, setRating] = useState<number>(0)
    const [feedbackComment, setFeedbackComment] = useState<string>('')
    const [feedbackSent, setFeedbackSent] = useState<boolean>(Boolean(currentOrder.feedback))
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const onSendFeedbackClick = () => {
        setIsLoading(true)
        currentOrder._id && currentUser._id && rating !== undefined &&
            api.postFeedback(rating, feedbackComment, currentOrder._id, currentUser._id)
                .then((data) => {
                    setIsLoading(false)
                    setFeedbackSent(true)
                    dispatch(orderSlice.actions.changeFeedbackId(data._id))
                    dispatch(orderSlice.actions.resetOrder())
                    dispatch(currentUserSlice.actions.setHasCurrentOrder(false))
                })
    }

    function skipFeedback() {
        if (currentOrder.status === OrderStatus.completed) {
            hideOrder(() => {
                navigate(appRoutes.menu)
            })
        }
    }

    function performBackAction(navigateToMenu: boolean) {
        if (currentOrder.status === OrderStatus.failed || currentOrder.status === OrderStatus.declined || currentOrder.status === OrderStatus.canceled) {
            hideOrder(() => {
                if (navigateToMenu) {
                    return navigate(appRoutes.menu)
                }
            })
        }

        if (navigateToMenu) {
            navigate(appRoutes.menu)
        }
    }

    function hideOrder(callback: () => void) {
        setIsLoading(true)
        api.hideOrder(currentOrder).then(() => {
            setIsLoading(false)
            dispatch(orderSlice.actions.resetOrder())
            dispatch(currentUserSlice.actions.setHasCurrentOrder(false))
            callback()
        })
    }

    const labels = {
        [PaymentMethod.online]: t`Online`,
        [PaymentMethod.cashInStore]: t`Cash in store`,
        [PaymentMethod.cardInStore]: t`Card in store`
    }

    const paymentMethod = PaymentMethod[currentOrder.paymentMethod as keyof typeof PaymentMethod];

    const green = '#24A44B'
    const yellow = '#FFB01D'

    let orderStatusColor = yellow
    if (currentOrder.status == OrderStatus.completed || currentOrder.status == OrderStatus.inProgress) {
        orderStatusColor = green
    }

    useEffect(() => {
        (!currentRestaurant || !currentUser._id || currentOrder.items.length === 0)
            && navigate(appRoutes.start)
    }, [currentRestaurant, currentUser, currentOrder, navigate])

    useEffect(() => {
        let updaterId: number | NodeJS.Timer;
        currentUser?._id && api.getCurrentOrder().then((order) => {
            dispatch(currentOrderSlice.actions.setOrderFromServer(order))
            updaterId = setInterval(
                () => api.getCurrentOrder().then((order) => dispatch(currentOrderSlice.actions.setOrderFromServer(order))),
                30000 // 30 sec
            )
            return () => clearInterval(updaterId)
        })
    }, [currentUser])

    return <>{currentOrder.items.length > 0
        ? <Box sx={{ height: '100vh' }}>
            <Backdrop
                sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}
                open={isLoading}
                onClick={() => { }}
            >
                <Loader />
            </Backdrop>
            <CheckoutHeader restaurantName={currentRestaurant!.name} onBackClick={() => performBackAction(false)} />
            <Box pb={'12px'} />
            <StyledPaper sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0 0 0', overflow: ' hidden', bgcolor: orderStatusColor }}>
                <StatusIndicator status={currentOrder.status} />
            </StyledPaper>
            <Box pb={'12px'} />
            {currentOrder.status === OrderStatus.completed &&
                <>{feedbackSent || currentOrder.feedback
                    ? <StyledPaper>
                        <Box display={'flex'}>
                            <Typography>{t`Feedback sent!`}</Typography>
                            <Box pr={'8px'} />
                            <CheckCircleOutlineIcon />
                        </Box>
                    </StyledPaper>
                    : <StyledPaper>
                        <Typography display={'flex'} justifyContent="center" alignItems={'center'} sx={{ mb: '16px' }}>{t`${currentUser.name}, we’d love to hear your feedback!`}</Typography>
                        <Box display={'flex'} justifyContent="center" flexGrow={1} sx={{ mb: '16px' }}>
                            <Rating
                                size="large"
                                name="simple-controlled"
                                value={rating}
                                onChange={(event, newValue) => {
                                    setRating(newValue ?? 0);
                                }}
                            />
                        </Box>
                        <TextField
                            InputProps={{
                                style: {
                                    borderRadius: "16px",
                                }
                            }}
                            fullWidth
                            multiline
                            rows={3}
                            color='secondary'
                            placeholder={t`Tell us about your experience...`}
                            value={feedbackComment}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setFeedbackComment((event.target as HTMLInputElement).value);
                            }}
                        />
                        {<Box display='flex' flexDirection='column' p='10px 0px 0px 0px' flexGrow={1} sx={{ mb: '16px' }}>
                            <StyledButton
                                size="large"
                                sx={{ height: '100%' }}
                                disabled={rating == 0}
                                variant={'contained'}
                                fullWidth
                                color='success'
                                onClick={onSendFeedbackClick}
                            >
                                {t`Send feedback`}
                                <Box pr={'8px'} />
                                <SendIcon />
                            </StyledButton>
                            <Box p={'6px'} />
                            <StyledButton
                                size="large"
                                variant={'outlined'}
                                fullWidth
                                color='info'
                                onClick={skipFeedback}
                            >
                                {t`Skip`}
                                <Box pr={'8px'} />
                            </StyledButton>
                        </Box>
                        }
                    </StyledPaper>
                }
                    <Box pb={'12px'} />
                </>}
            <StyledPaper>
                {currentOrder.items.map(item => <Box key={item._id}>
                    <OrderItemSummary item={item} />
                    <Box pb={'12px'} />
                </Box>)}
                {currentOrder.comment && <>
                    <Typography>{t`Comment`}</Typography>
                    <Typography>{currentOrder.comment}</Typography>
                </>}
                <Divider variant={'middle'} sx={{ margin: 0 }} />
                <Box display={'flex'}>
                    <Box flexGrow={1}>
                        <Typography>{t`Total price`}</Typography>
                    </Box>
                    <Box color={'secondary'}>
                        <PriceRender price={currentOrder.total} />
                    </Box>
                </Box>
            </StyledPaper>
            <Box pb={'12px'} />
            <StyledPaper>
                <Typography>{t`Where`}</Typography>
                {currentOrder.shippingMethod === ShippingMethod.takeout
                    ? <>
                        <Typography>{currentRestaurant?.address}</Typography>
                    </>
                    : <>
                        <Typography>{currentOrder.deliveryLocation?.address}</Typography>
                        <Typography>{currentOrder.deliveryLocation?.comment}</Typography>
                    </>}
                <Box pb={'12px'} />
                <Typography>{t`When`}</Typography>
                <Typography>
                    {`${dayjs(currentOrder.deadlineDate).format('HH:mm')}-${dayjs(currentOrder.deadlineDate).add(15, 'minute').format('HH:mm')}`}
                </Typography>
            </StyledPaper>
            <Box pb={'12px'} />
            <StyledPaper>
                <Typography>{t`Name`}</Typography>
                <Typography>{currentUser.name}</Typography>
                <Box pb={'12px'} />
                <Typography>{t`Phone number`}</Typography>
                <Typography>{currentUser.phoneNumber}</Typography>
            </StyledPaper>
            <Box pb={'12px'} />
            <StyledPaper>
                <Typography>{t`Payment type`}</Typography>
                <Typography>{labels[paymentMethod]}</Typography>
            </StyledPaper>
            <Box pb={'12px'} />
            <StyledButton
                variant={'contained'}
                fullWidth
                onClick={() => performBackAction(true)}
            >
                {t`Go to menu`}
            </StyledButton>
            <Box pb={'12px'} />
        </Box>
        : <Loader />}
    </>
}

export default OrderStatusScreen;